import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';

const STAGE_OPTIONS = ['Активно', 'Исполнено'];

// Поле ввода со стрелкой как у <select>, но с возможностью ввести любой текст.
// При клике по стрелке раскрывается весь список без учёта введённого текста,
// при выборе пункта текст полностью заменяется выбранным значением.
const SelectLikeInput = ({ name, value, onChange, placeholder, options }) => {
    const [open, setOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (open && highlightedIndex >= 0 && listRef.current) {
            const item = listRef.current.children[highlightedIndex];
            if (item) item.scrollIntoView({ block: 'nearest' });
        }
    }, [open, highlightedIndex]);

    const handleInputChange = (e) => {
        onChange(e);
        setOpen(true);
        setHighlightedIndex(-1);
    };

    const handleSelect = (option) => {
        onChange({ target: { name, value: option } });
        setOpen(false);
        setHighlightedIndex(-1);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) {
                setOpen(true);
                setHighlightedIndex(0);
            } else {
                setHighlightedIndex(prev => (prev + 1) % options.length);
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (open) {
                setHighlightedIndex(prev => (prev - 1 + options.length) % options.length);
            }
        } else if (e.key === 'Enter') {
            if (open && highlightedIndex >= 0) {
                e.preventDefault();
                handleSelect(options[highlightedIndex]);
            }
        } else if (e.key === 'Escape') {
            setOpen(false);
            setHighlightedIndex(-1);
        }
    };

    return (
        <div
            ref={containerRef}
            className="select-like-input-container"
            style={{ position: 'relative', width: '100%' }}
        >
            <input
                type="text"
                name={name}
                value={value}
                onChange={handleInputChange}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                autoComplete="off"
                style={{ width: '100%', paddingRight: '2rem' }}
            />
            <span
                className="select-like-input-arrow"
                onClick={() => {
                    setOpen(prev => !prev);
                    setHighlightedIndex(-1);
                }}
                style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    color: '#495057',
                    fontSize: '0.8rem',
                    lineHeight: 1,
                }}
            >
                {open ? '▲' : '▼'}
            </span>
            {open && (
                <ul
                    ref={listRef}
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        margin: 0,
                        padding: 0,
                        listStyle: 'none',
                        background: '#fff',
                        border: '1px solid #ced4da',
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                >
                    {options.map((option, index) => (
                        <li
                            key={option}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelect(option);
                            }}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            style={{
                                padding: '0.5rem',
                                cursor: 'pointer',
                                background: index === highlightedIndex ? '#e9ecef' : 'transparent',
                                fontSize: '0.9rem',
                            }}
                        >
                            {option}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const OrderModal = ({ order, onClose, onUpdate, isEditMode = false, accountNumber, onBackToAccount, user }) => {
    const [activeTab, setActiveTab] = useState('info');
    const [editMode, setEditMode] = useState(isEditMode);
    const [currentOrder, setCurrentOrder] = useState(order);
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [statementsLoading, setStatementsLoading] = useState(false);
    const [statements, setStatements] = useState([]);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [orderDetails, setOrderDetails] = useState([]);
    const [detailsMessage, setDetailsMessage] = useState('');
    
    // Состояние для пагинации выписок
    const [statementsPagination, setStatementsPagination] = useState({
        page: 1,
        pageSize: 25,
        totalCount: 0,
        totalPages: 1
    });
    
    // Новые состояния для автозаполнения
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [showAccountSuggestions, setShowAccountSuggestions] = useState(false);
    const [searchingAccounts, setSearchingAccounts] = useState(false);
    const [owners, setOwners] = useState([]);
    const [filteredOwners, setFilteredOwners] = useState([]);
    const [showOwnerSuggestions, setShowOwnerSuggestions] = useState(false);
    const [selectedAccountNumber, setSelectedAccountNumber] = useState(null);
    
    // Состояния для автозаполнения судов
    const [courts, setCourts] = useState([]);
    const [filteredCourts, setFilteredCourts] = useState([]);
    const [showCourtSuggestions, setShowCourtSuggestions] = useState(false);
    
    // Состояния для автозаполнения РОСП
    const [rospList, setRospList] = useState([]);
    const [filteredRosp, setFilteredRosp] = useState([]);
    const [showRospSuggestions, setShowRospSuggestions] = useState(false);

    // Функция для форматирования даты в формат input[type="date"]
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    // Загрузка собственников при изменении выбранного номера счета
    useEffect(() => {
        const loadOwners = async () => {
            if (selectedAccountNumber) {
                try {
                    const response = await api.getOwnersByAccountNumber(selectedAccountNumber);
                    if (response.data && response.data.owners && Array.isArray(response.data.owners)) {
                        const ownersList = response.data.owners;
                        setOwners(ownersList);
                        setFilteredOwners(ownersList);
                        // Автоматически заполняем первым собственником
                        if (ownersList.length > 0) {
                            const firstOwner = ownersList[0];
                            setFormData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    personName: firstOwner.name || '',
                                    personINN: firstOwner.inn || '',
                                    personID: firstOwner.id || ''
                                };
                            });
                        }
                    } else {
                        setOwners([]);
                        setFilteredOwners([]);
                    }
                } catch (error) {
                    console.error('Error loading owners:', error);
                    setOwners([]);
                    setFilteredOwners([]);
                }
            } else {
                setOwners([]);
                setFilteredOwners([]);
            }
        };

        loadOwners();
    }, [selectedAccountNumber]);

    // Загрузка списка судов при входе в режим редактирования
    useEffect(() => {
        const loadCourts = async () => {
            if (editMode) {
                try {
                    const response = await api.getCourts();
                    if (response.data && Array.isArray(response.data)) {
                        setCourts(response.data);
                        setFilteredCourts(response.data);
                    } else {
                        setCourts([]);
                        setFilteredCourts([]);
                    }
                } catch (error) {
                    console.error('Error loading courts:', error);
                    setCourts([]);
                    setFilteredCourts([]);
                }
            }
        };

        loadCourts();
    }, [editMode]);

    // Загрузка списка РОСП при входе в режим редактирования
    useEffect(() => {
        const loadRosp = async () => {
            if (editMode) {
                try {
                    const response = await api.getRosp();
                    if (response.data && Array.isArray(response.data)) {
                        setRospList(response.data);
                        setFilteredRosp(response.data);
                    } else {
                        setRospList([]);
                        setFilteredRosp([]);
                    }
                } catch (error) {
                    console.error('Error loading ROSP:', error);
                    setRospList([]);
                    setFilteredRosp([]);
                }
            }
        };

        loadRosp();
    }, [editMode]);

    useEffect(() => {
        if (currentOrder) {
            setFormData({
                // Основная информация
                id: currentOrder?.id || '',
                accountNumber: currentOrder?.accountNumber || '',
                executorName: currentOrder?.executorName || '',
                agentINN: currentOrder?.agentINN || '',
                documentType: currentOrder?.documentType || '',
                outNumber: currentOrder?.outNumber || '',
                documentDate: currentOrder?.documentDate ? formatDateForInput(currentOrder.documentDate) : '',

                // Период взыскания
                collectionPeriodFrom: currentOrder?.collectionPeriodFrom ? formatDateForInput(currentOrder.collectionPeriodFrom) : '',
                collectionPeriodTo: currentOrder?.collectionPeriodTo ? formatDateForInput(currentOrder.collectionPeriodTo) : '',

                // Суммы иска
                amountClaimFee: formatAmountForInput(currentOrder?.amountClaimFee),
                amountClaimPenalties: formatAmountForInput(currentOrder?.amountClaimPenalties),
                amountClaimToll: formatAmountForInput(currentOrder?.amountClaimToll),
                amountUnusedToll: formatAmountForInput(currentOrder?.amountUnusedToll),

                // Судебная информация
                nameCourt: currentOrder?.nameCourt || '',
                dateAcceptance: currentOrder?.dateAcceptance ? formatDateForInput(currentOrder.dateAcceptance) : '',
                caseNumber: currentOrder?.caseNumber || '',
                reviewResult: currentOrder?.reviewResult || '',

                // Взысканные суммы
                amountRecoveredFee: formatAmountForInput(currentOrder?.amountRecoveredFee),
                amountRecoveredPenalties: formatAmountForInput(currentOrder?.amountRecoveredPenalties),
                amountRecoveredToll: formatAmountForInput(currentOrder?.amountRecoveredToll),
                amountVoluntaryExecution: formatAmountForInput(currentOrder?.amountVoluntaryExecution),

                // Даты решения
                decisionDate: currentOrder?.decisionDate ? formatDateForInput(currentOrder.decisionDate) : '',
                effectiveDate: currentOrder?.effectiveDate ? formatDateForInput(currentOrder.effectiveDate) : '',

                // Апелляция и выдача
                appeal: currentOrder?.appeal || '',
                dateIssue: currentOrder?.dateIssue ? formatDateForInput(currentOrder.dateIssue) : '',
                sourceDocumentNumber: currentOrder?.sourceDocumentNumber || '',

                // Даты получения и исполнения
                dateReceipt: currentOrder?.dateReceipt ? formatDateForInput(currentOrder.dateReceipt) : '',
                dateExecution: currentOrder?.dateExecution ? formatDateForInput(currentOrder.dateExecution) : '',

                // Исполнительное производство
                institutionCollection: currentOrder?.institutionCollection || '',
                stage: currentOrder?.stage || '',
                amountEnforcementFee: formatAmountForInput(currentOrder?.amountEnforcementFee),
                amountEnforcementPenalties: formatAmountForInput(currentOrder?.amountEnforcementPenalties),
                amountEnforcementToll: formatAmountForInput(currentOrder?.amountEnforcementToll),

                // Исполнительные документы
                amountExecDocFee: formatAmountForInput(currentOrder?.amountExecDocFee),
                amountExecDocPenalties: formatAmountForInput(currentOrder?.amountExecDocPenalties),
                amountExecDocToll: formatAmountForInput(currentOrder?.amountExecDocToll),
                amountRefundedToll: formatAmountForInput(currentOrder?.amountRefundedToll),

                // Примечания
                note: currentOrder?.note || '',
                comments: currentOrder?.comments || '',

                // Person и Address
                personID: currentOrder?.person?.id || '',
                personName: currentOrder?.person?.name || '',
                personINN: currentOrder?.person?.inn || '',
                addressHome: currentOrder?.address?.home || '',
                addressApartment: currentOrder?.address?.apartment || ''
            });
        } else {
            // Пустая форма для создания нового приказа
            setFormData({
                accountNumber: accountNumber || '',
                executorName: '',
                agentINN: '',
                documentType: '',
                outNumber: '',
                documentDate: '',
                collectionPeriodFrom: '',
                collectionPeriodTo: '',
                amountClaimFee: '',
                amountClaimPenalties: '',
                amountClaimToll: '',
                amountUnusedToll: '',
                nameCourt: '',
                dateAcceptance: '',
                caseNumber: '',
                reviewResult: '',
                amountRecoveredFee: '',
                amountRecoveredPenalties: '',
                amountRecoveredToll: '',
                amountVoluntaryExecution: '',
                decisionDate: '',
                effectiveDate: '',
                appeal: '',
                dateIssue: '',
                sourceDocumentNumber: '',
                dateReceipt: '',
                dateExecution: '',
                institutionCollection: '',
                stage: '',
                amountEnforcementFee: '',
                amountEnforcementPenalties: '',
                amountEnforcementToll: '',
                amountExecDocFee: '',
                amountExecDocPenalties: '',
                amountExecDocToll: '',
                amountRefundedToll: '',
                note: '',
                comments: '',
                personName: '',
                personINN: '',
                addressHome: '',
                addressApartment: ''
            });
        }
    }, [currentOrder, accountNumber]);

    useEffect(() => {
        if (activeTab === 'statements' && currentOrder) {
            // При открытии вкладки сбрасываем на первую страницу
            setStatementsPagination(prev => ({ ...prev, page: 1 }));
            loadStatements(1);
        }
        if (activeTab === 'details' && currentOrder) {
            loadOrderDetails();
        }
    }, [activeTab, currentOrder]);

    // Генерация номеров страниц для пагинации выписок (как в Orders.js)
    const generateStatementsPageNumbers = () => {
        const pages = [];
        const current = statementsPagination.page;
        const total = statementsPagination.totalPages;

        if (total <= 7) {
            for (let i = 1; i <= total; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (current > 3) pages.push('...');
            const start = Math.max(2, current - 1);
            const end = Math.min(total - 1, current + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (current < total - 2) pages.push('...');
            pages.push(total);
        }

        return pages;
    };

    // Навигация по страницам выписок
    const goToStatementsPage = (page) => {
        if (page >= 1 && page <= statementsPagination.totalPages) {
            setStatementsPagination(prev => ({ ...prev, page }));
            loadStatements(page);
        }
    };

    const loadOrderDetails = async () => {
        if (!currentOrder) return;
        setDetailsLoading(true);
        setDetailsMessage('');
        try {
            const response = await api.getDetailsByOrder(currentOrder.id);
            setOrderDetails(response.data || []);
        } catch (error) {
            console.error('Error loading order details:', error);
            setDetailsMessage('Ошибка загрузки детализации');
        } finally {
            setDetailsLoading(false);
        }
    };
    
    const loadStatements = async (page) => {
        if (!currentOrder) return;

        const targetPage = page ?? statementsPagination.page;
        const pageSize = statementsPagination.pageSize;

        setStatementsLoading(true);
        try {
            const response = await api.getStatementsByOrderId(currentOrder.id, targetPage, pageSize);
            setStatements(response.data.data || []);
            setStatementsPagination(prev => ({
                ...prev,
                page: targetPage,
                totalCount: response.data.totalCount ?? 0,
                totalPages: response.data.totalPages ?? 1
            }));
        } catch (error) {
            console.error('Error loading Statements:', error);
            setMessage('Ошибка загрузки выписок');
        } finally {
            setStatementsLoading(false);
        }
    };

    // Удаление выписки
    const handleDeleteStatement = async (e, statementId) => {
        e.stopPropagation();
        if (!window.confirm('Вы уверены, что хотите удалить эту выписку?')) return;

        try {
            await api.deleteStatementSaved(statementId);
            setMessage('Выписка удалена успешно');
            loadStatements(); // Перезагружаем список выписок
        } catch (error) {
            console.error('Error deleting statement:', error);
            setMessage('Ошибка при удалении выписки');
        }
    };

    // Функция для рендеринга заголовка с хлебными крошками
    const renderHeaderTitle = () => {
        if (editMode) {
            return currentOrder?.id ? `Редактирование приказа № ${currentOrder.caseNumber || '...'}` : 'Создание приказа';
        }

        // Режим просмотра - показываем хлебные крошки
        if (accountNumber) {
            return (
                <div className="breadcrumb-header">
                    <span
                        className="breadcrumb-link"
                        onClick={handleBreadcrumbClick}
                        title="Перейти к лицевому счету"
                    >
                        Лицевой счет № {accountNumber}
                    </span>
                    <span className="breadcrumb-separator"> → </span>
                    <span>Приказ № {currentOrder?.caseNumber || '...'}</span>
                </div>
            );
        }

        // Если номер счета не передан, показываем простой заголовок
        return `Приказ № ${currentOrder?.caseNumber || '...'}`;
    };

    // Обработчик клика по хлебной крошке
    const handleBreadcrumbClick = () => {
        if (accountNumber) {
            onClose();
            if (onBackToAccount) {
                onBackToAccount();
            }
        }
    };

    // Обработчик изменения номера счета с автозаполнением
    const handleAccountNumberChange = async (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            accountNumber: value,
            personName: '', // Сбрасываем выбранного собственника
            personINN: '',
            personID: '',
            addressHome: '',
            addressApartment: ''
        }));

        if (value && value.length > 0) {
            setSearchingAccounts(true);
            try {
                const response = await api.searchAccountNumbers(value);
                if (response.data && Array.isArray(response.data)) {
                    setFilteredAccounts(response.data);
                    setShowAccountSuggestions(response.data.length > 0);
                } else {
                    setFilteredAccounts([]);
                    setShowAccountSuggestions(false);
                }
            } catch (error) {
                console.error('Error searching account numbers:', error);
                setFilteredAccounts([]);
                setShowAccountSuggestions(false);
            } finally {
                setSearchingAccounts(false);
            }
        } else {
            setFilteredAccounts([]);
            setShowAccountSuggestions(false);
        }
    };

    // Выбор номера счета из подсказок
    const handleSelectAccount = async (accountNum) => {
        setFormData(prev => ({
            ...prev,
            accountNumber: accountNum,
            personName: '',
            personINN: '',
            personID: '',
            addressHome: '',
            addressApartment: ''
        }));
        setShowAccountSuggestions(false);
        // Устанавливаем выбранный номер счёта - useEffect загрузит собственников
        setSelectedAccountNumber(accountNum);

        // Загружаем адрес по номеру счёта
        try {
            const accountData = await api.getAccountByNumber(accountNum);
            const account = accountData?.data ?? accountData;
            if (account) {
                setFormData(prev => ({
                    ...prev,
                    addressHome: account.home || account.address?.home || account.addressHome || '',
                    addressApartment: account.apartment || account.address?.apartment || account.addressApartment || ''
                }));
            }
        } catch (error) {
            console.error('Error loading account address:', error);
        }
    };

    // Обработчик изменения ФИО собственника с автозаполнением
    const handleOwnerNameChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            personName: value
        }));

        if (value && owners.length > 0) {
            const filtered = owners.filter(owner =>
                owner.name.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredOwners(filtered);
            setShowOwnerSuggestions(true);
        } else {
            setFilteredOwners(owners);
            setShowOwnerSuggestions(false);
        }
    };

    // Выбор собственника из подсказок
    const handleSelectOwner = (owner) => {
        setFormData(prev => ({
            ...prev,
            personName: owner.name,
            personINN: owner.inn || '',
            personID: owner.id || ''
        }));
        setShowOwnerSuggestions(false);
    };

    // Обработчик изменения названия суда с автозаполнением
    const handleCourtNameChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            nameCourt: value
        }));

        if (value && courts.length > 0) {
            const filtered = courts.filter(court =>
                court.name.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredCourts(filtered);
            setShowCourtSuggestions(true);
        } else {
            setFilteredCourts(courts);
            setShowCourtSuggestions(false);
        }
    };

    // Выбор суда из подсказок
    const handleSelectCourt = (courtName) => {
        setFormData(prev => ({
            ...prev,
            nameCourt: courtName
        }));
        setShowCourtSuggestions(false);
    };

    // Обработчик изменения учреждения взыскания с автозаполнением
    const handleRospChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            institutionCollection: value
        }));

        if (value && rospList.length > 0) {
            const filtered = rospList.filter(rosp =>
                rosp.name.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredRosp(filtered);
            setShowRospSuggestions(true);
        } else {
            setFilteredRosp(rospList);
            setShowRospSuggestions(false);
        }
    };

    // Выбор РОСП из подсказок
    const handleSelectRosp = (rospName) => {
        setFormData(prev => ({
            ...prev,
            institutionCollection: rospName
        }));
        setShowRospSuggestions(false);
    };

    // Форматирование суммы из числа в строку с запятой для отображения в поле ввода.
    // Нулевые значения возвращаются как пустая строка — они отображаются через placeholder.
    const formatAmountForInput = (val) => {
        if (val === null || val === undefined || val === '') return '';
        const num = Number(val);
        if (isNaN(num) || num === 0) return '';
        // Форматируем с двумя знаками после запятой, заменяем точку на запятую
        return num.toFixed(2).replace('.', ',');
    };

    // Поля с суммами (числовые)
    const amountFields = [
        'amountClaimFee', 'amountClaimPenalties', 'amountClaimToll', 'amountUnusedToll',
        'amountRecoveredFee', 'amountRecoveredPenalties', 'amountRecoveredToll', 'amountVoluntaryExecution',
        'amountEnforcementFee', 'amountEnforcementPenalties', 'amountEnforcementToll',
        'amountExecDocFee', 'amountExecDocPenalties', 'amountExecDocToll', 'amountRefundedToll'
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (amountFields.includes(name)) {
            // Разрешаем: цифры, запятую, точку, минус в начале
            // Нормализуем: заменяем точку на запятую
            let normalized = value.replace('.', ',');
            // Убираем всё лишнее (оставляем цифры, запятую, минус в начале)
            normalized = normalized.replace(/[^0-9,\-]/g, '');
            // Минус только в начале
            if (normalized.indexOf('-') > 0) {
                normalized = normalized.replace(/-/g, '');
            }
            // Только одна запятая
            const parts = normalized.split(',');
            if (parts.length > 2) {
                normalized = parts[0] + ',' + parts.slice(1).join('');
            }
            // Не более двух знаков после запятой
            if (parts.length === 2 && parts[1].length > 2) {
                normalized = parts[0] + ',' + parts[1].slice(0, 2);
            }
            setFormData(prev => ({ ...prev, [name]: normalized }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData) return;

        // Валидация обязательных полей
        if (!formData.accountNumber) {
            alert('Поле "Номер счета" обязательно для заполнения');
            return;
        }

        if (!formData.personName) {
            alert('Поле "ФИО собственника" обязательно для заполнения');
            return;
        }

        setLoading(true);
        try {
            // Функция для преобразования дат
            const parseDate = (dateStr) => {
                if (!dateStr) return null;
                return new Date(dateStr).toISOString();
            };

            // Вспомогательная функция: строка с запятой → число
            const parseAmount = (val) => {
                if (val === '' || val === null || val === undefined) return 0;
                return Number(String(val).replace(',', '.'));
            };

            // Подготовка данных для отправки
            const processedData = {
                id: formData.id ? parseInt(formData.id) : undefined,
                accountNumber: parseInt(formData.accountNumber),
                executorName: formData.executorName,
                agentINN: formData.agentINN,
                documentType: formData.documentType,
                outNumber: formData.outNumber,
                documentDate: parseDate(formData.documentDate),
                collectionPeriodFrom: parseDate(formData.collectionPeriodFrom),
                collectionPeriodTo: parseDate(formData.collectionPeriodTo),
                amountClaimFee: parseAmount(formData.amountClaimFee),
                amountClaimPenalties: parseAmount(formData.amountClaimPenalties),
                amountClaimToll: parseAmount(formData.amountClaimToll),
                amountUnusedToll: parseAmount(formData.amountUnusedToll),
                nameCourt: formData.nameCourt,
                dateAcceptance: parseDate(formData.dateAcceptance),
                caseNumber: formData.caseNumber,
                reviewResult: formData.reviewResult,
                amountRecoveredFee: parseAmount(formData.amountRecoveredFee),
                amountRecoveredPenalties: parseAmount(formData.amountRecoveredPenalties),
                amountRecoveredToll: parseAmount(formData.amountRecoveredToll),
                amountVoluntaryExecution: parseAmount(formData.amountVoluntaryExecution),
                decisionDate: parseDate(formData.decisionDate),
                effectiveDate: parseDate(formData.effectiveDate),
                appeal: formData.appeal,
                dateIssue: parseDate(formData.dateIssue),
                sourceDocumentNumber: formData.sourceDocumentNumber,
                dateReceipt: parseDate(formData.dateReceipt),
                dateExecution: parseDate(formData.dateExecution),
                institutionCollection: formData.institutionCollection,
                amountEnforcementFee: parseAmount(formData.amountEnforcementFee),
                amountEnforcementPenalties: parseAmount(formData.amountEnforcementPenalties),
                amountEnforcementToll: parseAmount(formData.amountEnforcementToll),
                stage: formData.stage,
                amountExecDocFee: parseAmount(formData.amountExecDocFee),
                amountExecDocPenalties: parseAmount(formData.amountExecDocPenalties),
                amountExecDocToll: parseAmount(formData.amountExecDocToll),
                amountRefundedToll: parseAmount(formData.amountRefundedToll),
                note: formData.note,
                comments: formData.comments,
                // Объекты Person и Address
                person: formData.personName ? {
                    id: formData.personID ? parseInt(formData.personID) : undefined,
                    name: formData.personName,
                    inn: formData.personINN || ""
                } : null
            };
            // Явно устанавливаем null для пустых объектов
            if (processedData.address === null) {
                processedData.address = null;
            }
            
            // Удаляем поля с null/undefined значениями (кроме числовых id)
            Object.keys(processedData).forEach(key => {
                if (key === 'id') return; // id должен быть числом или отсутствовать
                if (processedData[key] === null || processedData[key] === undefined) {
                    processedData[key] = "";
                }
            });
            // Удаляем id если он undefined (создание нового приказа)
            if (processedData.id === undefined) {
                delete processedData.id;
            }
            // Удаляем id из person если он undefined
            if (processedData.person && processedData.person.id === undefined) {
                delete processedData.person.id;
            }

            if (currentOrder && currentOrder.id) {
                const updatedOrderResponse = await api.updateOrder(currentOrder.id, processedData);
                console.log('[OrderModal] updateOrder raw response:', updatedOrderResponse);
                // Получаем свежие данные приказа после обновления
                const freshResponse = await api.getOrderById(currentOrder.id);
                console.log('[OrderModal] getOrderById after update:', freshResponse);
                setCurrentOrder(freshResponse.data || freshResponse);
                setMessage('Приказ обновлен успешно');
            } else {
                const createdOrderResponse = await api.createOrder(processedData);
                console.log('[OrderModal] createOrder raw response:', createdOrderResponse);
                // Извлекаем id из ответа:
                // сервер может вернуть { data: { id, ... } }, { data: 123 }, { id: ... } или просто число
                const responseData = createdOrderResponse?.data ?? createdOrderResponse;
                const newId = responseData && typeof responseData === 'object'
                    ? (responseData.id ?? responseData.Id)
                    : responseData;
                console.log('[OrderModal] new order id:', newId, 'type:', typeof newId);
                if (!newId) {
                    throw new Error('Сервер не вернул ID созданного приказа. Ответ: ' + JSON.stringify(createdOrderResponse));
                }
                // Получаем полный объект приказа по id
                const freshResponse = await api.getOrderById(newId);
                console.log('[OrderModal] getOrderById after create:', freshResponse);
                setCurrentOrder(freshResponse.data || freshResponse);
                setMessage('Приказ создан успешно');
            }

            if (onUpdate) {
                onUpdate();
            }

            // Выходим из режима редактирования после успешного сохранения
            setEditMode(false);

        } catch (error) {
            setMessage('Ошибка сохранения приказа');
            console.error('Save order error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!currentOrder?.id) return;

        if (!window.confirm('Вы уверены, что хотите удалить этот приказ?')) {
            return;
        }

        try {
            await api.deleteOrder(currentOrder.id);
            setMessage('Приказ удален успешно');

            if (onUpdate) {
                onUpdate();
            }

            setTimeout(() => {
                onClose();
            }, 1000);

        } catch (error) {
            setMessage('Ошибка удаления приказа');
            console.error('Delete order error:', error);
        }
    };

    // Функции форматирования для отображения
    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        return new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(Number(value));
    };

    const formatDateToRussian = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    };

    if (!formData) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{renderHeaderTitle()}</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <div className="modal-tabs">
                    <div className="tabs-header">
                        <button
                            className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
                            onClick={() => setActiveTab('info')}
                        >
                            Инфо
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'statements' ? 'active' : ''}`}
                            onClick={() => setActiveTab('statements')}
                        >
                            Выписки
                        </button>
                        {currentOrder && (
                            <button
                                className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
                                onClick={() => setActiveTab('details')}
                            >
                                Детализация
                            </button>
                        )}
                    </div>

                    <div className="tab-content">
                        {activeTab === 'info' && (
                            <div className="info-tab">
                                {message && (
                                    <div className={`message ${message.includes('Ошибка') ? 'error' : 'success'}`}>
                                        {message}
                                    </div>
                                )}

                                <div className="tab-actions">
                                    {!editMode ? (
                                        <>
                                            {user && (user.role === 'admin' || user.role === 'lawyer') && (
                                                <>
                                                    <button
                                                        onClick={() => setEditMode(true)}
                                                        className="btn-primary"
                                                    >
                                                        Редактировать
                                                    </button>
                                                    <button
                                                        onClick={handleDelete}
                                                        className="btn-delete-modal"
                                                    >
                                                        Удалить
                                                    </button>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <div className="edit-actions">
                                            <button
                                                onClick={handleSubmit}
                                                disabled={loading}
                                                className="btn-primary"
                                            >
                                                {loading ? 'Сохранение...' : 'Сохранить'}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    // Если это новый приказ (нет currentOrder) - просто закрываем
                                                    if (!currentOrder) {
                                                        onClose();
                                                        return;
                                                    }
                                                    setEditMode(false);
                                                    // Сброс формы к исходным данным
                                                    if (currentOrder) {
                                                        setFormData({
                                                            id: currentOrder?.id || '',
                                                            accountNumber: currentOrder?.accountNumber || '',
                                                            executorName: currentOrder?.executorName || '',
                                                            agentINN: currentOrder?.agentINN || '',
                                                            documentType: currentOrder?.documentType || '',
                                                            outNumber: currentOrder?.outNumber || '',
                                                            documentDate: currentOrder?.documentDate ? formatDateForInput(currentOrder.documentDate) : '',
                                                            collectionPeriodFrom: currentOrder?.collectionPeriodFrom ? formatDateForInput(currentOrder.collectionPeriodFrom) : '',
                                                            collectionPeriodTo: currentOrder?.collectionPeriodTo ? formatDateForInput(currentOrder.collectionPeriodTo) : '',
                                                            amountClaimFee: formatAmountForInput(currentOrder?.amountClaimFee),
                                                            amountClaimPenalties: formatAmountForInput(currentOrder?.amountClaimPenalties),
                                                            amountClaimToll: formatAmountForInput(currentOrder?.amountClaimToll),
                                                            amountUnusedToll: formatAmountForInput(currentOrder?.amountUnusedToll),
                                                            nameCourt: currentOrder?.nameCourt || '',
                                                            dateAcceptance: currentOrder?.dateAcceptance ? formatDateForInput(currentOrder.dateAcceptance) : '',
                                                            caseNumber: currentOrder?.caseNumber || '',
                                                            reviewResult: currentOrder?.reviewResult || '',
                                                            amountRecoveredFee: formatAmountForInput(currentOrder?.amountRecoveredFee),
                                                            amountRecoveredPenalties: formatAmountForInput(currentOrder?.amountRecoveredPenalties),
                                                            amountRecoveredToll: formatAmountForInput(currentOrder?.amountRecoveredToll),
                                                            amountVoluntaryExecution: formatAmountForInput(currentOrder?.amountVoluntaryExecution),
                                                            decisionDate: currentOrder?.decisionDate ? formatDateForInput(currentOrder.decisionDate) : '',
                                                            effectiveDate: currentOrder?.effectiveDate ? formatDateForInput(currentOrder.effectiveDate) : '',
                                                            appeal: currentOrder?.appeal || '',
                                                            dateIssue: currentOrder?.dateIssue ? formatDateForInput(currentOrder.dateIssue) : '',
                                                            sourceDocumentNumber: currentOrder?.sourceDocumentNumber || '',
                                                            dateReceipt: currentOrder?.dateReceipt ? formatDateForInput(currentOrder.dateReceipt) : '',
                                                            dateExecution: currentOrder?.dateExecution ? formatDateForInput(currentOrder.dateExecution) : '',
                                                            institutionCollection: currentOrder?.institutionCollection || '',
                                                            stage: currentOrder?.stage || '',
                                                            amountEnforcementFee: formatAmountForInput(currentOrder?.amountEnforcementFee),
                                                            amountEnforcementPenalties: formatAmountForInput(currentOrder?.amountEnforcementPenalties),
                                                            amountEnforcementToll: formatAmountForInput(currentOrder?.amountEnforcementToll),
                                                            amountExecDocFee: formatAmountForInput(currentOrder?.amountExecDocFee),
                                                            amountExecDocPenalties: formatAmountForInput(currentOrder?.amountExecDocPenalties),
                                                            amountExecDocToll: formatAmountForInput(currentOrder?.amountExecDocToll),
                                                            amountRefundedToll: formatAmountForInput(currentOrder?.amountRefundedToll),
                                                            note: currentOrder?.note || '',
                                                            comments: currentOrder?.comments || '',
                                                            personID: currentOrder?.person?.id || '',
                                                            personName: currentOrder?.person?.name || '',
                                                            personINN: currentOrder?.person?.inn || '',
                                                            addressHome: currentOrder?.address?.home || '',
                                                            addressApartment: currentOrder?.address?.apartment || ''
                                                        });
                                                    } else {
                                                        // Для нового приказа - закрываем модальное окно
                                                        onClose();
                                                        return;
                                                    }
                                                    setSelectedAccountNumber(null);
                                                    setOwners([]);
                                                    setFilteredOwners([]);
                                                }}
                                                className="btn-secondary"
                                            >
                                                Отмена
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {editMode ? (
                                    <form onSubmit={handleSubmit} className="order-form">
                                        <div className="form-sections">
                                            {/* Системная информация */}
                                            {currentOrder?.id && (
                                                <div className="form-section">
                                                    <h4>Системная информация</h4>
                                                    <div className="form-row">
                                                        <div className="form-group">
                                                            <label>ID приказа:</label>
                                                            <input
                                                                type="text"
                                                                value={currentOrder.id}
                                                                disabled
                                                                style={{
                                                                    backgroundColor: '#f8f9fa',
                                                                    color: '#6c757d',
                                                                    cursor: 'not-allowed'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Основная информация */}
                                            <div className="form-section">
                                                <h4>Основная информация</h4>
                                                <div className="form-row">
                                                    <div className="form-group" style={{ position: 'relative' }}>
                                                        <label>Номер счета *:</label>
                                                        <input
                                                            type="text"
                                                            name="accountNumber"
                                                            value={formData?.accountNumber || ''}
                                                            onChange={handleAccountNumberChange}
                                                            onFocus={() => {
                                                                if (formData && formData.accountNumber && filteredAccounts.length > 0) {
                                                                    setShowAccountSuggestions(true);
                                                                }
                                                            }}
                                                            onBlur={() => {
                                                                setTimeout(() => setShowAccountSuggestions(false), 200);
                                                            }}
                                                            placeholder="Введите номер счета"
                                                            required
                                                            autoComplete="off"
                                                        />
                                                        {searchingAccounts && (
                                                            <div className="autocomplete-suggestions">
                                                                <div className="autocomplete-item" style={{ textAlign: 'center', color: '#666' }}>
                                                                    Поиск...
                                                                </div>
                                                            </div>
                                                        )}
                                                        {!searchingAccounts && showAccountSuggestions && filteredAccounts.length > 0 && (
                                                            <div className="autocomplete-suggestions">
                                                                {filteredAccounts.slice(0, 10).map((acc) => (
                                                                    <div
                                                                        key={acc.number}
                                                                        className="autocomplete-item"
                                                                        onClick={() => handleSelectAccount(acc.number)}
                                                                    >
                                                                        {acc.number}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {!searchingAccounts && formData && formData.accountNumber && filteredAccounts.length === 0 && showAccountSuggestions && (
                                                            <div className="autocomplete-suggestions">
                                                                <div className="autocomplete-item" style={{ textAlign: 'center', color: '#999' }}>
                                                                    Счета не найдены
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Адрес дома:</label>
                                                        <input
                                                            type="text"
                                                            name="addressHome"
                                                            value={formData.addressHome}
                                                            readOnly
                                                            placeholder="Заполняется автоматически"
                                                            style={{ backgroundColor: '#f8f9fa', color: '#6c757d', cursor: 'not-allowed' }}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Квартира:</label>
                                                        <input
                                                            type="text"
                                                            name="addressApartment"
                                                            value={formData.addressApartment}
                                                            readOnly
                                                            placeholder="Заполняется автоматически"
                                                            style={{ backgroundColor: '#f8f9fa', color: '#6c757d', cursor: 'not-allowed' }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Информация о собственнике - перенесена вверх */}
                                            <div className="form-section">
                                                <h4>Информация о собственнике</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>ФИО собственника *:</label>
                                                        {owners.length > 1 ? (
                                                            <select
                                                                name="personName"
                                                                value={formData.personName}
                                                                onChange={(e) => {
                                                                    const selectedOwner = owners.find(owner => owner.name === e.target.value);
                                                                    if (selectedOwner) {
                                                                        handleSelectOwner(selectedOwner);
                                                                    }
                                                                }}
                                                                required
                                                                disabled={!formData || !formData.accountNumber}
                                                            >
                                                                {owners.map((owner) => (
                                                                    <option key={owner.id} value={owner.name}>
                                                                        {owner.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                name="personName"
                                                                value={formData.personName}
                                                                onChange={handleChange}
                                                                placeholder="ФИО собственника"
                                                                required
                                                                disabled={!formData || !formData.accountNumber}
                                                                readOnly={owners.length === 1}
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="form-group">
                                                        <label>ИНН собственника:</label>
                                                        <input
                                                            type="text"
                                                            name="personINN"
                                                            value={formData.personINN}
                                                            onChange={handleChange}
                                                            placeholder="Введите ИНН собственника"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Дополнительная основная информация */}
                                            <div className="form-section">
                                                <h4>Дополнительная информация</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Исполнитель:</label>
                                                        <input
                                                            type="text"
                                                            name="executorName"
                                                            value={formData.executorName}
                                                            onChange={handleChange}
                                                            placeholder="Введите исполнителя"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>ИНН агента:</label>
                                                        <input
                                                            type="text"
                                                            name="agentINN"
                                                            value={formData.agentINN}
                                                            onChange={handleChange}
                                                            placeholder="Введите ИНН агента"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Тип документа:</label>
                                                        <input
                                                            type="text"
                                                            name="documentType"
                                                            value={formData.documentType}
                                                            onChange={handleChange}
                                                            placeholder="Введите тип документа"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Исходящий номер:</label>
                                                        <input
                                                            type="text"
                                                            name="outNumber"
                                                            value={formData.outNumber}
                                                            onChange={handleChange}
                                                            placeholder="Введите исходящий номер"
                                                        />
                                                    </div>
                                                     <div className="form-group">
                                                        <label>Дата документа:</label>
                                                        <input
                                                            type="date"
                                                            name="documentDate"
                                                            value={formData.documentDate}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Период взыскания */}
                                            <div className="form-section">
                                                <h4>Период взыскания</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Период взыскания с:</label>
                                                        <input
                                                            type="date"
                                                            name="collectionPeriodFrom"
                                                            value={formData.collectionPeriodFrom}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Период взыскания по:</label>
                                                        <input
                                                            type="date"
                                                            name="collectionPeriodTo"
                                                            value={formData.collectionPeriodTo}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Финансовые данные - Иск */}
                                            <div className="form-section">
                                                <h4>Суммы иска</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Сумма иска (взнос):</label>
                                                        <input
                                                            type="text"
                                                            name="amountClaimFee"
                                                            value={formData.amountClaimFee}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Сумма иска (пени):</label>
                                                        <input
                                                            type="text"
                                                            name="amountClaimPenalties"
                                                            value={formData.amountClaimPenalties}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Сумма иска (пошлина):</label>
                                                        <input
                                                            type="text"
                                                            name="amountClaimToll"
                                                            value={formData.amountClaimToll}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Неиспользованная пошлина:</label>
                                                        <input
                                                            type="text"
                                                            name="amountUnusedToll"
                                                            value={formData.amountUnusedToll}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Судебная информация */}
                                            <div className="form-section">
                                                <h4>Судебная информация</h4>
                                                <div className="form-row">
                                                    <div className="form-group" style={{ position: 'relative' }}>
                                                        <label>Суд:</label>
                                                        <input
                                                            type="text"
                                                            name="nameCourt"
                                                            value={formData.nameCourt}
                                                            onChange={handleCourtNameChange}
                                                            onFocus={() => {
                                                                if (formData && formData.nameCourt && filteredCourts.length > 0) {
                                                                    setShowCourtSuggestions(true);
                                                                }
                                                            }}
                                                            onBlur={() => {
                                                                setTimeout(() => setShowCourtSuggestions(false), 200);
                                                            }}
                                                            placeholder="Введите название суда"
                                                            autoComplete="off"
                                                        />
                                                        {showCourtSuggestions && filteredCourts.length > 0 && (
                                                            <div className="autocomplete-suggestions">
                                                                {filteredCourts.slice(0, 10).map((court, index) => (
                                                                    <div
                                                                        key={index}
                                                                        className="autocomplete-item"
                                                                        onClick={() => handleSelectCourt(court.name)}
                                                                    >
                                                                        {court.name}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Дата принятия судом заявления:</label>
                                                        <input
                                                            type="date"
                                                            name="dateAcceptance"
                                                            value={formData.dateAcceptance}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Номер дела:</label>
                                                        <input
                                                            type="text"
                                                            name="caseNumber"
                                                            value={formData.caseNumber}
                                                            onChange={handleChange}
                                                            placeholder="Введите номер дела"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Результат рассмотрения:</label>
                                                        <input
                                                            type="text"
                                                            name="reviewResult"
                                                            value={formData.reviewResult}
                                                            onChange={handleChange}
                                                            placeholder="Введите результат"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Финансовые данные - Взыскано */}
                                            <div className="form-section">
                                                <h4>Взысканные суммы</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Взыскано (взнос):</label>
                                                        <input
                                                            type="text"
                                                            name="amountRecoveredFee"
                                                            value={formData.amountRecoveredFee}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Взыскано (пени):</label>
                                                        <input
                                                            type="text"
                                                            name="amountRecoveredPenalties"
                                                            value={formData.amountRecoveredPenalties}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Взыскано (пошлина):</label>
                                                        <input
                                                            type="text"
                                                            name="amountRecoveredToll"
                                                            value={formData.amountRecoveredToll}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Добровольное исполнение:</label>
                                                        <input
                                                            type="text"
                                                            name="amountVoluntaryExecution"
                                                            value={formData.amountVoluntaryExecution}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Даты решения */}
                                            <div className="form-section">
                                                <h4>Даты решения</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Дата принятия решения (вынесения судебного приказа):</label>
                                                        <input
                                                            type="date"
                                                            name="decisionDate"
                                                            value={formData.decisionDate}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Дата вступления в силу судебного приказа (решения):</label>
                                                        <input
                                                            type="date"
                                                            name="effectiveDate"
                                                            value={formData.effectiveDate}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Информация об апелляции */}
                                            <div className="form-section">
                                                <h4>Апелляция и выдача</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Статус документа:</label>
                                                        <input
                                                            type="text"
                                                            name="appeal"
                                                            value={formData.appeal}
                                                            onChange={handleChange}
                                                            placeholder="Введите Статус документа (Исполнено/Активно/Отмена)"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Дата выдачи исполнительного (листа/приказа/решения):</label>
                                                        <input
                                                            type="date"
                                                            name="dateIssue"
                                                            value={formData.dateIssue}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Номер исполнительного:</label>
                                                        <input
                                                            type="text"
                                                            name="sourceDocumentNumber"
                                                            value={formData.sourceDocumentNumber}
                                                            onChange={handleChange}
                                                            placeholder="Введите номер исполнительного листа/приказа/решения"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Даты получения и исполнения */}
                                            <div className="form-section">
                                                <h4>Даты получения и исполнения</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Дата получения ИД взыскателем:</label>
                                                        <input
                                                            type="date"
                                                            name="dateReceipt"
                                                            value={formData.dateReceipt}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Дата направления на исполнение:</label>
                                                        <input
                                                            type="date"
                                                            name="dateExecution"
                                                            value={formData.dateExecution}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Исполнительное производство */}
                                            <div className="form-section">
                                                <h4>Исполнительное производство</h4>
                                                <div className="form-row">
                                                    <div className="form-group" style={{ position: 'relative' }}>
                                                        <label>Учреждение взыскания:</label>
                                                        <input
                                                            type="text"
                                                            name="institutionCollection"
                                                            value={formData.institutionCollection}
                                                            onChange={handleRospChange}
                                                            onFocus={() => {
                                                                if (formData && formData.institutionCollection && filteredRosp.length > 0) {
                                                                    setShowRospSuggestions(true);
                                                                }
                                                            }}
                                                            onBlur={() => {
                                                                setTimeout(() => setShowRospSuggestions(false), 200);
                                                            }}
                                                            placeholder="Введите учреждение"
                                                            autoComplete="off"
                                                        />
                                                        {showRospSuggestions && filteredRosp.length > 0 && (
                                                            <div className="autocomplete-suggestions">
                                                                {filteredRosp.slice(0, 10).map((rosp, index) => (
                                                                    <div
                                                                        key={index}
                                                                        className="autocomplete-item"
                                                                        onClick={() => handleSelectRosp(rosp.name)}
                                                                    >
                                                                        {rosp.name}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Стадия:</label>
                                                        <SelectLikeInput
                                                            name="stage"
                                                            value={formData.stage}
                                                            onChange={handleChange}
                                                            placeholder="Введите стадию"
                                                            options={STAGE_OPTIONS}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Исполнительный сбор (взнос):</label>
                                                        <input
                                                            type="text"
                                                            name="amountEnforcementFee"
                                                            value={formData.amountEnforcementFee}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Исполнительный сбор (пени):</label>
                                                        <input
                                                            type="text"
                                                            name="amountEnforcementPenalties"
                                                            value={formData.amountEnforcementPenalties}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Исполнительный сбор (пошлина):</label>
                                                        <input
                                                            type="text"
                                                            name="amountEnforcementToll"
                                                            value={formData.amountEnforcementToll}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Исполнительные документы */}
                                            <div className="form-section">
                                                <h4>Исполнительные документы</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Испол. док. (взнос):</label>
                                                        <input
                                                            type="text"
                                                            name="amountExecDocFee"
                                                            value={formData.amountExecDocFee}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Испол. док. (пени):</label>
                                                        <input
                                                            type="text"
                                                            name="amountExecDocPenalties"
                                                            value={formData.amountExecDocPenalties}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Испол. док. (пошлина):</label>
                                                        <input
                                                            type="text"
                                                            name="amountExecDocToll"
                                                            value={formData.amountExecDocToll}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Возвращенная пошлина:</label>
                                                        <input
                                                            type="text"
                                                            name="amountRefundedToll"
                                                            value={formData.amountRefundedToll}
                                                            onChange={handleChange}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Примечания */}
                                            <div className="form-section">
                                                <h4>Примечания</h4>
                                                <div className="form-row">
                                                    <div className="form-group full-width">
                                                        <label>Примечание:</label>
                                                        <textarea
                                                            name="note"
                                                            value={formData.note}
                                                            onChange={handleChange}
                                                            placeholder="Введите примечание"
                                                            rows="3"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group full-width">
                                                        <label>Комментарии:</label>
                                                        <textarea
                                                            name="comments"
                                                            value={formData.comments}
                                                            onChange={handleChange}
                                                            placeholder="Введите комментарии"
                                                            rows="3"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                ) : currentOrder ? (
                                    <div className="order-details">
                                        <div className="details-grid">
                                            <div className="detail-group">
                                                <h4>Основная информация</h4>
                                                <div className="detail-item">
                                                    <label>Номер счета:</label>
                                                    <span>{currentOrder.accountNumber || '-'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Исполнитель:</label>
                                                    <span>{currentOrder.executorName || '-'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Тип документа:</label>
                                                    <span>{currentOrder.documentType || '-'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Дата документа:</label>
                                                    <span>{formatDateToRussian(currentOrder.documentDate)}</span>
                                                </div>
                                            </div>

                                            <div className="detail-group">
                                                <h4>Взысканная судом сумма</h4>                                            
                                                <div className="detail-item">
                                                    <label>Основной долг:</label>
                                                    <span>{formatCurrency(currentOrder.amountRecoveredFee)}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Пени:</label>
                                                    <span>{formatCurrency(currentOrder.amountRecoveredPenalties)}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Пошлина:</label>
                                                    <span>{formatCurrency(currentOrder.amountRecoveredToll)}</span>
                                                </div>
                                            </div>

                                            <div className="detail-group">
                                                <h4>Судебная информация</h4>
                                                <div className="detail-item">
                                                    <label>Суд:</label>
                                                    <span>{currentOrder.nameCourt || '-'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Номер дела:</label>
                                                    <span>{currentOrder.caseNumber || '-'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Результат:</label>
                                                    <span>{currentOrder.reviewResult || '-'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Дата решения:</label>
                                                    <span>{formatDateToRussian(currentOrder.decisionDate)}</span>
                                                </div>
                                            </div>

                                            {currentOrder.note && (
                                                <div className="detail-group full-width">
                                                    <h4>Примечание</h4>
                                                    <div className="detail-item">
                                                        <span>{currentOrder.note}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )}

                        {activeTab === 'details' && (
                            <div className="statements-tab">
                                {detailsMessage && (
                                    <div className={`message ${detailsMessage.includes('Ошибка') ? 'error' : 'success'}`}>
                                        {detailsMessage}
                                    </div>
                                )}
                                {detailsLoading ? (
                                    <div className="loading">Загрузка детализации...</div>
                                ) : orderDetails.length === 0 ? (
                                    <div className="no-data">Нет данных по детализации</div>
                                ) : (
                                    <div className="table-wrapper" style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}>
                                        <table className="statements-table">
                                            <thead>
                                                <tr>
                                                    <th rowSpan="2" style={{ textAlign: 'center', verticalAlign: 'middle', width: '100px' }}>
                                                        Дата
                                                    </th>
                                                    <th rowSpan="2" style={{ textAlign: 'center', verticalAlign: 'middle', width: '80px' }}>
                                                        Номер
                                                    </th>
                                                    <th rowSpan="2" style={{ textAlign: 'center', verticalAlign: 'middle', width: '100px' }}>
                                                        Кредит
                                                    </th>
                                                    <th colSpan="4" style={{ textAlign: 'center', borderBottom: '1px solid #dee2e6', borderLeft: '2px solid #6c757d' }}>
                                                        До операции
                                                    </th>
                                                    <th colSpan="3" style={{ textAlign: 'center', borderBottom: '1px solid #dee2e6', borderLeft: '2px solid #6c757d' }}>
                                                        Расписано
                                                    </th>
                                                    <th colSpan="4" style={{ textAlign: 'center', borderBottom: '1px solid #dee2e6', borderLeft: '2px solid #6c757d' }}>
                                                        После операции
                                                    </th>
                                                </tr>
                                                <tr>
                                                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap', borderLeft: '2px solid #6c757d' }}>Основной</th>
                                                    <th style={{ textAlign: 'right' }}>Пени</th>
                                                    <th style={{ textAlign: 'right' }}>Пошлина</th>
                                                    <th style={{ textAlign: 'right' }}>Итого</th>
                                                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap', borderLeft: '2px solid #6c757d' }}>Основной</th>
                                                    <th style={{ textAlign: 'right' }}>Пени</th>
                                                    <th style={{ textAlign: 'right' }}>Пошлина</th>
                                                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap', borderLeft: '2px solid #6c757d' }}>Основной</th>
                                                    <th style={{ textAlign: 'right' }}>Пени</th>
                                                    <th style={{ textAlign: 'right' }}>Пошлина</th>
                                                    <th style={{ textAlign: 'right' }}>Итого</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orderDetails.map((item, index) => (
                                                    <tr key={index}>
                                                        <td style={{ textAlign: 'center' }}>{formatDateToRussian(item.date)}</td>
                                                        <td style={{ textAlign: 'center' }}>{item.number || '-'}</td>
                                                        <td className="number-cell">{formatCurrency(item.credit)}</td>
                                                        <td className="number-cell" style={{ borderLeft: '2px solid #6c757d' }}>{formatCurrency(item.feeBefore)}</td>
                                                        <td className="number-cell">{formatCurrency(item.penaltyBefore)}</td>
                                                        <td className="number-cell">{formatCurrency(item.tollBefore)}</td>
                                                        <td className="number-cell">{formatCurrency(item.totalBefore)}</td>
                                                        <td className="number-cell" style={{ borderLeft: '2px solid #6c757d' }}>{formatCurrency(item.feeDivided)}</td>
                                                        <td className="number-cell">{formatCurrency(item.penaltyDivided)}</td>
                                                        <td className="number-cell">{formatCurrency(item.tollDivided)}</td>
                                                        <td className="number-cell" style={{ borderLeft: '2px solid #6c757d' }}>{formatCurrency(item.feeAfter)}</td>
                                                        <td className="number-cell">{formatCurrency(item.penaltyAfter)}</td>
                                                        <td className="number-cell">{formatCurrency(item.tollAfter)}</td>
                                                        <td className="number-cell">{formatCurrency(item.totalAfter)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'statements' && (
                            <div className="statements-tab" style={{ paddingBottom: '0.1rem' }}>
                                <h4>Выписки по приказу</h4>

                                {/* Пагинация сверху */}
                                <div className="pagination-top" style={{ paddingBottom: '0.5rem', paddingTop: '0.5rem'}}>
                                    <div className="pagination-info">
                                        Страница {statementsPagination.page} из {statementsPagination.totalPages}
                                        ({statementsPagination.totalCount} всего записей)
                                    </div>

                                    <div className="pagination-controls">
                                        <button
                                            onClick={() => goToStatementsPage(1)}
                                            disabled={statementsPagination.page === 1}
                                            className="btn-pagination btn-pagination-first"
                                        >
                                            ⟪
                                        </button>
                                        <button
                                            onClick={() => goToStatementsPage(statementsPagination.page - 1)}
                                            disabled={statementsPagination.page === 1}
                                            className="btn-pagination btn-pagination-prev"
                                        >
                                            ⟨
                                        </button>

                                        {generateStatementsPageNumbers().map((pageNum, index) => (
                                            pageNum === '...' ? (
                                                <span key={index} className="pagination-ellipsis">...</span>
                                            ) : (
                                                <button
                                                    key={index}
                                                    onClick={() => goToStatementsPage(pageNum)}
                                                    className={`btn-pagination ${statementsPagination.page === pageNum ? 'btn-pagination-active' : 'btn-pagination-inactive'}`}
                                                >
                                                    {pageNum}
                                                </button>
                                            )
                                        ))}

                                        <button
                                            onClick={() => goToStatementsPage(statementsPagination.page + 1)}
                                            disabled={statementsPagination.page === statementsPagination.totalPages}
                                            className="btn-pagination btn-pagination-next"
                                        >
                                            ⟩
                                        </button>
                                        <button
                                            onClick={() => goToStatementsPage(statementsPagination.totalPages)}
                                            disabled={statementsPagination.page === statementsPagination.totalPages}
                                            className="btn-pagination btn-pagination-last"
                                        >
                                            ⟫
                                        </button>
                                    </div>
                                </div>

                                {statementsLoading ? (
                                    <div className="loading">Загрузка выписок...</div>
                                ) : statements.length === 0 ? (
                                    <div className="no-data">Нет выписок по этому приказу</div>
                                ) : (
                                    <div className="table-wrapper" style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'auto', flexShrink: 0 }}>
                                        <table className="statements-table">
                                            <thead>
                                                <tr style={{ height: '50px', minHeight: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Дата
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Номер
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Вид операции
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '200px', minWidth: '200px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Контрагент
                                                    </th>
                                                    {/* <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        ИНН контрагента
                                                    </th> */}
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        БИК банка контрагента
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Счет контрагента
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Кредит
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '400px', minWidth: '400px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Назначение
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Тип
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Примечание
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Действия
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {statements.map(statement => (
                                                    <tr
                                                        key={statement.id}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td>{formatDateToRussian(statement.date)}</td>
                                                        <td>{statement.number}</td>
                                                        <td>{statement.typeOperation}</td>
                                                        <td>{statement.counterparty}</td>
                                                        {/* <td>{statement.counterpartyINN}</td> */}
                                                        <td>{statement.counterpartyBIK}</td>
                                                        <td>{statement.counterpartyAccount}</td>
                                                        <td className="number-cell">{formatCurrency(statement.credit)}</td>
                                                        <td>{statement.destination}</td>
                                                        <td>{statement.typedest}</td>
                                                        <td>{statement.comment}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            {user && (user.role === 'admin' || user.role === 'accountant') && (
                                                                <button
                                                                    onClick={(e) => handleDeleteStatement(e, statement.id)}
                                                                    className="btn-delete"
                                                                    title="Удалить"
                                                                >
                                                                    <span>🗑️</span>
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Пагинация снизу */}
                                <div className="pagination-bottom" style={{ paddingBottom: '0.5rem', paddingTop: '0.5rem'}}>
                                    <div className="pagination-info">
                                        Страница {statementsPagination.page} из {statementsPagination.totalPages}
                                        ({statementsPagination.totalCount} всего записей)
                                    </div>

                                    <div className="pagination-controls">
                                        <button
                                            onClick={() => goToStatementsPage(1)}
                                            disabled={statementsPagination.page === 1}
                                            className="btn-pagination btn-pagination-first"
                                        >
                                            ⟪
                                        </button>
                                        <button
                                            onClick={() => goToStatementsPage(statementsPagination.page - 1)}
                                            disabled={statementsPagination.page === 1}
                                            className="btn-pagination btn-pagination-prev"
                                        >
                                            ⟨
                                        </button>

                                        {generateStatementsPageNumbers().map((pageNum, index) => (
                                            pageNum === '...' ? (
                                                <span key={index} className="pagination-ellipsis">...</span>
                                            ) : (
                                                <button
                                                    key={index}
                                                    onClick={() => goToStatementsPage(pageNum)}
                                                    className={`btn-pagination ${statementsPagination.page === pageNum ? 'btn-pagination-active' : 'btn-pagination-inactive'}`}
                                                >
                                                    {pageNum}
                                                </button>
                                            )
                                        ))}

                                        <button
                                            onClick={() => goToStatementsPage(statementsPagination.page + 1)}
                                            disabled={statementsPagination.page === statementsPagination.totalPages}
                                            className="btn-pagination btn-pagination-next"
                                        >
                                            ⟩
                                        </button>
                                        <button
                                            onClick={() => goToStatementsPage(statementsPagination.totalPages)}
                                            disabled={statementsPagination.page === statementsPagination.totalPages}
                                            className="btn-pagination btn-pagination-last"
                                        >
                                            ⟫
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderModal;
